import { Badge } from "@/components/ui/badge";
import { ChevronRight } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { GrandChildItem } from "./GrandChildItem";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";

type SubCategoryItemProps = {
  child: {
    id: number;
    name: string;
    index: string;
    title?: string;
    description?: string;
    node_1?: string;
    node_2?: string;
    product_count?: string;
    url?: string;
    cat_text?: string;
  };
  isExpanded: boolean;
  onToggle: () => void;
  selectedUpdate: string;
};

export const SubCategoryItem = ({ child, isExpanded, onToggle, selectedUpdate }: SubCategoryItemProps) => {
  const [expandedGrandChildren, setExpandedGrandChildren] = useState<string[]>([]);

  const { data: grandChildCategories } = useQuery({
    queryKey: ["grandchild-categories", child.name, isExpanded, selectedUpdate],
    enabled: isExpanded,
    queryFn: async () => {
      let query = supabase
        .from("categories_atp_autoteile_de")
        .select("*")
        .eq("level", 3)
        .eq("parent_node_name", child.name);

      if (selectedUpdate && selectedUpdate !== "all") {
        const [hour, date] = selectedUpdate.split('h ');
        const [day, month, year] = date.split('-');
        const startDate = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day),
          parseInt(hour)
        );
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 1);

        query = query
          .gte('created_at', startDate.toISOString())
          .lt('created_at', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const toggleGrandChild = (grandChildName: string) => {
    setExpandedGrandChildren((prev) =>
      prev.includes(grandChildName)
        ? prev.filter((name) => name !== grandChildName)
        : [...prev, grandChildName]
    );
  };

  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
          <ChevronRight
            className={`h-5 w-5 transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          />
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="font-medium">
                  {child.url ? (
                    <a 
                      href={child.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {child.name}
                    </a>
                  ) : (
                    child.name
                  )}
                </div>
                <Badge 
                  variant={child.index === "index, follow" ? "default" : "destructive"}
                >
                  {child.index}
                </Badge>
                <Badge
                  variant={child.cat_text ? "default" : "secondary"}
                  className={child.cat_text ? "bg-green-500" : "bg-red-200"}
                >
                  {child.cat_text ? "Found content" : "no content"}
                </Badge>
              </div>
              {child.product_count && (
                <div className="text-sm text-muted-foreground">
                  Products: {child.product_count}
                </div>
              )}
            </div>
            {child.title && (
              <p className="text-sm text-muted-foreground text-left font-medium">{child.title}</p>
            )}
            {child.description && (
              <p className="text-sm text-muted-foreground text-left">{child.description}</p>
            )}
            <div className="text-sm text-muted-foreground">
              {(child.node_1 || child.node_2) && (
                <Breadcrumb>
                  <BreadcrumbList>
                    {child.node_1 && (
                      <>
                        <BreadcrumbItem>
                          <BreadcrumbLink href="#">{child.node_1}</BreadcrumbLink>
                        </BreadcrumbItem>
                        {child.node_2 && <BreadcrumbSeparator />}
                      </>
                    )}
                    {child.node_2 && (
                      <BreadcrumbItem>
                        <BreadcrumbLink href="#">{child.node_2}</BreadcrumbLink>
                      </BreadcrumbItem>
                    )}
                  </BreadcrumbList>
                </Breadcrumb>
              )}
            </div>
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-8 space-y-2 mt-2">
          {grandChildCategories?.map((grandChild) => (
            <GrandChildItem
              key={grandChild.id}
              grandChild={grandChild}
              isExpanded={expandedGrandChildren.includes(grandChild.name)}
              onToggle={() => toggleGrandChild(grandChild.name)}
              selectedUpdate={selectedUpdate}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};